import { CellContentPosition, Checkbox, CostEstimationButton, Icon, IconButton, Loader, Stack, TableCell, TableRow } from "@tm/components"
import { useCostEstimationModuleParameters } from "@tm/context-distribution"
import { CustomRepairTimeItemResponse, useCustomRepairTimesDefaults } from "@tm/data/basket"
import { useLocalization } from "@tm/localization"
import { getCategoryOfWorkDescriptionTextId, useVatRateText } from "@tm/utils"
import { useMemo } from "react"

type OwnRepairTimeItemProps = {
    repairTime: CustomRepairTimeItemResponse
    isSelected?: boolean
    isDeleting?: boolean
    onAddToCostEstimation(article: CustomRepairTimeItemResponse): void
    onEdit(article: CustomRepairTimeItemResponse): void
    onToggle(article: CustomRepairTimeItemResponse): void
    onDelete(article: CustomRepairTimeItemResponse): void
}

export function OwnRepairTimeItem({ repairTime, isSelected, isDeleting, onAddToCostEstimation, onDelete, onEdit, onToggle }: OwnRepairTimeItemProps) {
    const { translateText, formatCurrency, formatNumber } = useLocalization()
    const { customRepairTimesDefaults } = useCustomRepairTimesDefaults()
    const { defaultCurrencySymbol, defaultCurrencyCode, defaultRepairTimeDivision } = customRepairTimesDefaults ?? {}
    const { vatRateTypeDisplayShortText } = useVatRateText()
    const { hasCostEstimation } = useCostEstimationModuleParameters()

    const price = useMemo(() => {
        let finalPrice
        if (repairTime.fixedPriceValue) {
            finalPrice = repairTime.fixedPriceValue
        } else if (repairTime.workTime && repairTime.customHourlyRate && defaultRepairTimeDivision) {
            finalPrice = (repairTime.workTime * repairTime.customHourlyRate) / defaultRepairTimeDivision
        }
        return !!finalPrice && formatCurrency(finalPrice, defaultCurrencySymbol ?? defaultCurrencyCode ?? "")
    }, [
        defaultCurrencyCode,
        defaultCurrencySymbol,
        defaultRepairTimeDivision,
        repairTime.customHourlyRate,
        repairTime.fixedPriceValue,
        repairTime.workTime,
    ])

    const selectedVatRate = repairTime.vatRates?.find((vatRate) => vatRate.isSelected)

    function handleAddToCostEstimation() {
        onAddToCostEstimation(repairTime)
    }

    function handleEdit() {
        onEdit(repairTime)
    }

    function handleDelete() {
        onDelete(repairTime)
    }

    function handleToggle() {
        onToggle(repairTime)
    }

    return (
        <TableRow key={repairTime.id} colCount={8}>
            <TableCell>{repairTime.workId}</TableCell>
            <TableCell>{repairTime.description}</TableCell>
            <TableCell>{translateText(getCategoryOfWorkDescriptionTextId(repairTime.category))}</TableCell>
            <TableCell alignContent={CellContentPosition.right}>
                {repairTime.customHourlyRate && formatCurrency(repairTime.customHourlyRate, defaultCurrencySymbol ?? defaultCurrencyCode ?? "")}
            </TableCell>
            <TableCell alignContent={CellContentPosition.right}>{repairTime.workTime && formatNumber(repairTime.workTime, 2)}</TableCell>
            <TableCell alignContent={CellContentPosition.right}>{price}</TableCell>
            <TableCell alignContent={CellContentPosition.right} sx={{ pr: 3 }}>
                {!!selectedVatRate &&
                    vatRateTypeDisplayShortText({
                        vatRate: selectedVatRate.vatValue,
                        vatType: selectedVatRate.vatType,
                    })}
            </TableCell>
            <TableCell alignContent={CellContentPosition.right} sx={{ borderLeft: "1px solid #ccc", pl: 2 }}>
                <Stack direction="row" gap={1} alignItems="center">
                    <IconButton title={translateText(122)} onClick={handleEdit}>
                        <Icon name="edit_change" />
                    </IconButton>
                    <IconButton title={translateText(624)} onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loader size="small" /> : <Icon name="delete" />}
                    </IconButton>
                    {hasCostEstimation && (
                        <CostEstimationButton
                            title={translateText(916)}
                            onAddToCostEstimation={handleAddToCostEstimation}
                            color="highlight"
                            startIcon={<Icon name="add-repair-times" />}
                            sx={{ width: "65px" }}
                        />
                    )}
                    <Checkbox checked={isSelected} onChange={handleToggle} />
                </Stack>
            </TableCell>
        </TableRow>
    )
}
